import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CircleMarker, Tooltip } from 'react-leaflet';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { driverRecordShape } from './driverRecordShape';
import { formatClockTime } from '../../../utils/displayFormat';
import { DEFAULT_POLL_INTERVAL_MS } from '../../../hooks/useDriverLocationsPoll';
import mapOverlayCard from './mapOverlayStyles';
import legendStyles from './legendStyles';
import { STATUS_TIER_LABEL, accentClassVariants } from './statusStyles';
import { toggleSetValue } from './monitoringFilters';
import Button from '../../../components/atoms/forms/Button';
import { strideForBudget } from './trailGeometry';
import {
  GLOBAL_TRAIL_DOT_BUDGET,
  buildTrailLayers,
  totalTrailPoints,
} from './trailLayers';
import TrailHoverCard from './TrailHoverCard';
import MonitoringMapShell from './MonitoringMapShell';
import buildDriverMarkers from './buildDriverMarkers';
import PollCountdown from './PollCountdown';
import RouteForecastList from './RouteForecastList';
import CollapsibleSidePanel from './CollapsibleSidePanel';
import DriverCardsList from './DriverCardsList';

// Global Location View: every filtered driver's live pin over their recorded
// GPS trail (no stop markers or predicted lines — those belong to the
// Single-route view) beside a collapsible panel of route cards. Selecting a
// route from a pin popup or a card hands off to the parent, which swaps in
// RouteMapPane.

const PANEL_STORAGE_KEY = 'monitoring:route-panel-open';

// The legend's clickable route-status filters, in legend order. An empty
// selection shows every route; otherwise only the selected tiers keep their
// pins and trails.
const ROUTE_STATUS_FILTERS = ['on-track', 'behind', 'off-track'];

const hasPosition = (driver) =>
  Number.isFinite(driver.latitude) && Number.isFinite(driver.longitude);

const StyledDiv = styledComponent('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: theme.spacing.sectionGap,
  alignItems: 'stretch',

  '& .gmp-map-card': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    minHeight: theme.sizes.mapPaneMinHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  '& .gmp-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.gapXl,
    padding: theme.spacing.cellPadding,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    background: theme.colors.bgCard,
  },
  '& .gmp-title': {
    fontSize: theme.sizes.subheadFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .gmp-subtitle': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontVariantNumeric: 'tabular-nums',
    marginTop: theme.spacing.gapXs,
  },
  ...legendStyles('gmp'),
  // Status pills: a dot in the route's status color (the same accent the
  // pins use).
  ...accentClassVariants('gmp-swatch-dot', 'background'),
  '& .gmp-map-wrap': {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  // Top-right, clear of Leaflet's zoom control in the top-left corner (its
  // control layer sits at the same z-index as this card, and later DOM
  // order would paint the buttons over it) and of the Recenter button at
  // the bottom-right.
  '& .gmp-overlay': {
    ...mapOverlayCard(),
    top: theme.spacing.gapXl,
    right: theme.spacing.gapXl,
    padding: theme.spacing.paddingSm,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapLg,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .gmp-overlay-value': {
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .gmp-overlay-value.is-late': {
    color: theme.colors.statusOffTrack,
  },
  '& .gmp-overlay-sep': {
    color: theme.colors.textHint,
  },
  // Empty state when no driver has reported a position: the pins-only map
  // would otherwise read as broken. Centered over the map, click-through so
  // the map still pans underneath.
  '& .gmp-empty': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
    pointerEvents: 'none',
    background: `${theme.colors.white}EB`,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.radii.inner,
    boxShadow: theme.shadows.card,
    padding: theme.spacing.paddingMd,
    maxWidth: theme.sizes.maxWidthSm,
    textAlign: 'center',
  },
  '& .gmp-empty-title': {
    fontSize: theme.sizes.bodyFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .gmp-empty-sub': {
    marginTop: theme.spacing.gapSm,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    lineHeight: 1.4,
  },
});

function GlobalMapPane({
  drivers,
  lastPolledAt,
  isLoading,
  error,
  pollIntervalMs,
  fitKey,
  onSelectRoute,
  onSelectStop,
  onViewBreakdown,
}) {
  // The recorded reading under the cursor while it rides a trail — with the
  // driver whose trail it is, since this map shows many at once.
  const [hoverPing, setHoverPing] = useState(null);
  // Legend filter (map-only): the status tiers whose routes stay visible.
  // Empty = every route. The panel's cards keep listing the whole fleet.
  const [statusFilter, setStatusFilter] = useState(() => new Set());
  const visibleDrivers = useMemo(
    () =>
      statusFilter.size > 0
        ? drivers.filter((driver) => statusFilter.has(driver.status))
        : drivers,
    [drivers, statusFilter],
  );

  // Every visible route's recorded trail, drawn at once. One dot budget is
  // split across ALL trails (per-route budgets would still hand Leaflet tens
  // of thousands of markers on a busy region). Memoized on the drivers, not
  // the hover — `setHoverPing` is a stable setter.
  const trailLayers = useMemo(() => {
    const dotStride = strideForBudget(
      totalTrailPoints(visibleDrivers),
      GLOBAL_TRAIL_DOT_BUDGET,
    );
    const polylines = [];
    const gapMarkers = [];
    const trailDots = [];
    const startMarkers = [];
    visibleDrivers.forEach((driver) => {
      const pingTrail = driver.pingTrail || [];
      if (pingTrail.length === 0) {
        return;
      }
      const layers = buildTrailLayers(driver, pingTrail, {
        onTrailHover: (point) => setHoverPing(point ? { driver, point } : null),
        dotStride,
      });
      polylines.push(...layers.trailPolylines, ...layers.gapPolylines);
      gapMarkers.push(...layers.gapMarkers);
      trailDots.push(...layers.trailDots);
      if (layers.startMarker) {
        startMarkers.push(layers.startMarker);
      }
    });
    return { polylines, gapMarkers, trailDots, startMarkers };
  }, [visibleDrivers]);

  const pinCount = visibleDrivers.filter(hasPosition).length;
  const countLabel =
    pinCount > 0
      ? `${pinCount} ${pinCount === 1 ? 'driver' : 'drivers'}`
      : `${visibleDrivers.length} ${
          visibleDrivers.length === 1 ? 'route' : 'routes'
        }`;
  const subtitle = lastPolledAt
    ? `Updated ${formatClockTime(lastPolledAt, {
        hour: '2-digit',
      })} · ${countLabel}`
    : countLabel;

  const overlayStats = useMemo(() => {
    const lateCount = visibleDrivers.filter(
      (driver) => driver.status === 'behind' || driver.status === 'off-track',
    ).length;
    let stocked = 0;
    let total = 0;
    visibleDrivers.forEach((driver) => {
      stocked += driver.stopsCompleted || 0;
      total += driver.stopsTotal || 0;
    });
    return { routesShown: visibleDrivers.length, lateCount, stocked, total };
  }, [visibleDrivers]);

  return (
    <StyledDiv>
      <div className="gmp-map-card">
        <div className="gmp-header">
          <div>
            <div className="gmp-title">Live drivers</div>
            <div className="gmp-subtitle">{subtitle}</div>
          </div>
          <PollCountdown
            lastPolledAt={lastPolledAt}
            intervalMs={pollIntervalMs}
            isLoading={isLoading}
          />
        </div>
        <div className="gmp-legend" aria-label="Map legend" role="group">
          <span className="gmp-legend-item">
            <span className="gmp-swatch-line" /> Driven
          </span>
          <span className="gmp-legend-item">
            <span className="gmp-swatch-ring" /> Trail start
          </span>
          <span className="gmp-legend-item">
            <span className="gmp-swatch-line faint" />
            <span className="gmp-swatch-diamond" /> GPS gap
          </span>
          {ROUTE_STATUS_FILTERS.map((status) => (
            <Button
              key={status}
              variant="legend-pill"
              pressed={statusFilter.has(status)}
              title={`Show only ${STATUS_TIER_LABEL[
                status
              ].toLowerCase()} routes`}
              onClick={() =>
                setStatusFilter((current) => toggleSetValue(current, status))
              }
            >
              <span className={`gmp-swatch-dot ${status}`} />
              {STATUS_TIER_LABEL[status]}
            </Button>
          ))}
        </div>
        <div className="gmp-map-wrap">
          {overlayStats.routesShown > 0 && (
            <div className="gmp-overlay" aria-live="polite">
              <span>
                <span className="gmp-overlay-value">
                  {overlayStats.routesShown}
                </span>{' '}
                {overlayStats.routesShown === 1 ? 'route' : 'routes'} shown
              </span>
              <span className="gmp-overlay-sep">·</span>
              <span>
                <span
                  className={`gmp-overlay-value${
                    overlayStats.lateCount > 0 ? ' is-late' : ''
                  }`}
                >
                  {overlayStats.lateCount}
                </span>{' '}
                late
              </span>
              {overlayStats.total > 0 && (
                <>
                  <span className="gmp-overlay-sep">·</span>
                  <span>
                    <span className="gmp-overlay-value">
                      {overlayStats.stocked} / {overlayStats.total}
                    </span>{' '}
                    stops delivered
                  </span>
                </>
              )}
            </div>
          )}
          {pinCount === 0 && visibleDrivers.length > 0 && (
            <div className="gmp-empty" role="status">
              <div className="gmp-empty-title">
                No live driver positions yet
              </div>
              <div className="gmp-empty-sub">
                Drivers appear here once their app reports a location. Open a
                route from the panel to see its stops.
              </div>
            </div>
          )}
          <MonitoringMapShell fitTargets={visibleDrivers} fitKey={fitKey}>
            {trailLayers.polylines}
            {trailLayers.trailDots}
            {trailLayers.gapMarkers}
            {trailLayers.startMarkers}
            {hoverPing ? (
              <CircleMarker
                center={[hoverPing.point.latitude, hoverPing.point.longitude]}
                radius={6}
                color={theme.colors.white}
                weight={2}
                fillColor={theme.colors.routeLine}
                fillOpacity={1}
                interactive={false}
              >
                <Tooltip permanent direction="top" offset={[0, -8]}>
                  <TrailHoverCard
                    driver={hoverPing.driver}
                    point={hoverPing.point}
                  />
                </Tooltip>
              </CircleMarker>
            ) : null}
            {buildDriverMarkers(visibleDrivers, { onViewRoute: onSelectRoute })}
          </MonitoringMapShell>
        </div>
        <RouteForecastList drivers={drivers} selectedRouteId={null} />
      </div>
      <CollapsibleSidePanel
        title="Routes"
        count={drivers.length}
        storageKey={PANEL_STORAGE_KEY}
      >
        <DriverCardsList
          drivers={drivers}
          isLoading={isLoading}
          error={error}
          onSelectRoute={onSelectRoute}
          onSelectStop={onSelectStop}
          onViewBreakdown={onViewBreakdown}
        />
      </CollapsibleSidePanel>
    </StyledDiv>
  );
}

GlobalMapPane.propTypes = {
  drivers: PropTypes.arrayOf(driverRecordShape).isRequired,
  lastPolledAt: PropTypes.instanceOf(Date),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
  pollIntervalMs: PropTypes.number,
  fitKey: PropTypes.string.isRequired,
  onSelectRoute: PropTypes.func.isRequired,
  // (routeId, stopId) from a driver-card stop click.
  onSelectStop: PropTypes.func,
  onViewBreakdown: PropTypes.func,
};

GlobalMapPane.defaultProps = {
  lastPolledAt: null,
  isLoading: false,
  error: null,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
  onSelectStop: null,
  onViewBreakdown: null,
};

export default GlobalMapPane;
